/**
 * SKOP Price Alerts System
 *
 * Allows users to set price alerts for tokens and get notified
 * when price targets are hit.
 */

import { getTokenPrice } from './api';

// ============================================================================
// Types
// ============================================================================

export type AlertType = 'above' | 'below' | 'percent_up' | 'percent_down';
export type AlertStatus = 'active' | 'triggered' | 'expired' | 'cancelled';

export interface PriceAlert {
  id: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  type: AlertType;
  targetPrice?: number;      // For above/below
  targetPercent?: number;    // For percent_up/percent_down
  basePrice?: number;        // Price when alert was created (for % alerts)
  currentPrice?: number;     // Last checked price
  status: AlertStatus;
  createdAt: number;
  triggeredAt?: number;
  expiresAt?: number;        // Optional expiration
  walletAddress: string;
  notified: boolean;         // Whether browser notification was sent
}

export interface AlertConfig {
  enabled: boolean;
  soundEnabled: boolean;
  browserNotifications: boolean;
  pollIntervalMs: number;
}

// ============================================================================
// Storage
// ============================================================================

const ALERTS_KEY = 'skop_price_alerts';
const ALERTS_CONFIG_KEY = 'skop_alerts_config';

/**
 * Get all alerts for a wallet
 */
export function getAlerts(walletAddress: string): PriceAlert[] {
  try {
    const stored = localStorage.getItem(`${ALERTS_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save alerts for a wallet
 */
function saveAlerts(walletAddress: string, alerts: PriceAlert[]): void {
  localStorage.setItem(
    `${ALERTS_KEY}_${walletAddress}`,
    JSON.stringify(alerts)
  );
}

/**
 * Get alert configuration
 */
export function getAlertConfig(): AlertConfig {
  try {
    const stored = localStorage.getItem(ALERTS_CONFIG_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}

  return {
    enabled: true,
    soundEnabled: true,
    browserNotifications: true,
    pollIntervalMs: 30000, // 30 seconds
  };
}

/**
 * Save alert configuration
 */
export function saveAlertConfig(config: AlertConfig): void {
  localStorage.setItem(ALERTS_CONFIG_KEY, JSON.stringify(config));
}

// ============================================================================
// Alert Management
// ============================================================================

/**
 * Create a new price alert
 */
export function createAlert(params: {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  type: AlertType;
  targetPrice?: number;
  targetPercent?: number;
  currentPrice: number;
  walletAddress: string;
  expiresInHours?: number;
}): PriceAlert {
  const {
    tokenAddress,
    tokenSymbol,
    tokenName,
    type,
    targetPrice,
    targetPercent,
    currentPrice,
    walletAddress,
    expiresInHours,
  } = params;

  const alert: PriceAlert = {
    id: `alert_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    tokenAddress,
    tokenSymbol,
    tokenName,
    type,
    targetPrice,
    targetPercent,
    basePrice: currentPrice,
    currentPrice,
    status: 'active',
    createdAt: Date.now(),
    expiresAt: expiresInHours ? Date.now() + expiresInHours * 60 * 60 * 1000 : undefined,
    walletAddress,
    notified: false,
  };

  const alerts = getAlerts(walletAddress);
  alerts.unshift(alert);
  saveAlerts(walletAddress, alerts);

  return alert;
}

/**
 * Cancel an alert
 */
export function cancelAlert(walletAddress: string, alertId: string): void {
  const alerts = getAlerts(walletAddress);
  const updated = alerts.map(a =>
    a.id === alertId ? { ...a, status: 'cancelled' as AlertStatus } : a
  );
  saveAlerts(walletAddress, updated);
}

/**
 * Delete an alert
 */
export function deleteAlert(walletAddress: string, alertId: string): void {
  const alerts = getAlerts(walletAddress);
  const filtered = alerts.filter(a => a.id !== alertId);
  saveAlerts(walletAddress, filtered);
}

/**
 * Get active alerts for a token
 */
export function getActiveAlertsForToken(
  walletAddress: string,
  tokenAddress: string
): PriceAlert[] {
  return getAlerts(walletAddress).filter(
    a => a.tokenAddress === tokenAddress && a.status === 'active'
  );
}

/**
 * Get all active alerts
 */
export function getActiveAlerts(walletAddress: string): PriceAlert[] {
  return getAlerts(walletAddress).filter(a => a.status === 'active');
}

// ============================================================================
// Alert Checking
// ============================================================================

/**
 * Check if an alert should trigger based on current price
 */
export function checkAlertTrigger(alert: PriceAlert, currentPrice: number): boolean {
  if (alert.status !== 'active') return false;

  // Check expiration
  if (alert.expiresAt && Date.now() > alert.expiresAt) {
    return false;
  }

  switch (alert.type) {
    case 'above':
      return alert.targetPrice !== undefined && currentPrice >= alert.targetPrice;

    case 'below':
      return alert.targetPrice !== undefined && currentPrice <= alert.targetPrice;

    case 'percent_up':
      if (!alert.basePrice || !alert.targetPercent) return false;
      const upTarget = alert.basePrice * (1 + alert.targetPercent / 100);
      return currentPrice >= upTarget;

    case 'percent_down':
      if (!alert.basePrice || !alert.targetPercent) return false;
      const downTarget = alert.basePrice * (1 - alert.targetPercent / 100);
      return currentPrice <= downTarget;

    default:
      return false;
  }
}

/**
 * Check all active alerts and trigger notifications
 */
export async function checkAlerts(walletAddress: string): Promise<PriceAlert[]> {
  const config = getAlertConfig();
  if (!config.enabled) return [];

  const activeAlerts = getActiveAlerts(walletAddress);
  if (activeAlerts.length === 0) return [];

  // Group alerts by token to minimize API calls
  const tokenAlerts = new Map<string, PriceAlert[]>();
  for (const alert of activeAlerts) {
    const existing = tokenAlerts.get(alert.tokenAddress) || [];
    existing.push(alert);
    tokenAlerts.set(alert.tokenAddress, existing);
  }

  const triggeredAlerts: PriceAlert[] = [];
  const allAlerts = getAlerts(walletAddress);

  // Check each token
  for (const [tokenAddress, alerts] of tokenAlerts) {
    try {
      const priceData = await getTokenPrice(tokenAddress);
      if (!priceData) continue;

      const currentPrice = priceData.priceUsd;

      for (const alert of alerts) {
        // Check expiration
        if (alert.expiresAt && Date.now() > alert.expiresAt) {
          const idx = allAlerts.findIndex(a => a.id === alert.id);
          if (idx !== -1) {
            allAlerts[idx].status = 'expired';
          }
          continue;
        }

        // Update current price
        const idx = allAlerts.findIndex(a => a.id === alert.id);
        if (idx !== -1) {
          allAlerts[idx].currentPrice = currentPrice;
        }

        // Check trigger
        if (checkAlertTrigger(alert, currentPrice)) {
          if (idx !== -1) {
            allAlerts[idx].status = 'triggered';
            allAlerts[idx].triggeredAt = Date.now();
          }
          triggeredAlerts.push({ ...alert, currentPrice, status: 'triggered' });
        }
      }
    } catch (e) {
      console.warn('Failed to check price for alerts:', tokenAddress, e);
    }
  }

  // Save updated alerts
  saveAlerts(walletAddress, allAlerts);

  // Send notifications for triggered alerts
  if (triggeredAlerts.length > 0 && config.browserNotifications) {
    for (const alert of triggeredAlerts) {
      if (!alert.notified) {
        sendAlertNotification(alert);
        // Mark as notified
        const idx = allAlerts.findIndex(a => a.id === alert.id);
        if (idx !== -1) {
          allAlerts[idx].notified = true;
        }
      }
    }
    saveAlerts(walletAddress, allAlerts);
  }

  return triggeredAlerts;
}

// ============================================================================
// Notifications
// ============================================================================

/**
 * Request browser notification permission
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

/**
 * Send browser notification for triggered alert
 */
export function sendAlertNotification(alert: PriceAlert): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const config = getAlertConfig();

  let title = '';
  let body = '';

  switch (alert.type) {
    case 'above':
      title = `${alert.tokenSymbol} hit target!`;
      body = `Price reached $${alert.currentPrice?.toFixed(6)} (target: $${alert.targetPrice?.toFixed(6)})`;
      break;
    case 'below':
      title = `${alert.tokenSymbol} dropped!`;
      body = `Price fell to $${alert.currentPrice?.toFixed(6)} (target: $${alert.targetPrice?.toFixed(6)})`;
      break;
    case 'percent_up':
      title = `${alert.tokenSymbol} up ${alert.targetPercent}%!`;
      body = `Price: $${alert.currentPrice?.toFixed(6)} (from $${alert.basePrice?.toFixed(6)})`;
      break;
    case 'percent_down':
      title = `${alert.tokenSymbol} down ${alert.targetPercent}%!`;
      body = `Price: $${alert.currentPrice?.toFixed(6)} (from $${alert.basePrice?.toFixed(6)})`;
      break;
  }

  const notification = new Notification(title, {
    body,
    icon: '/favicon.ico',
    tag: alert.id,
    requireInteraction: true,
  });

  notification.onclick = () => {
    window.focus();
    window.location.href = `/${alert.tokenAddress}`;
    notification.close();
  };

  // Play sound if enabled
  if (config.soundEnabled) {
    playAlertSound();
  }
}

/**
 * Play alert sound
 */
function playAlertSound(): void {
  try {
    // Create a simple beep using Web Audio API
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (e) {
    console.warn('Could not play alert sound:', e);
  }
}

// ============================================================================
// Alert Polling Service
// ============================================================================

let pollInterval: number | null = null;

/**
 * Start polling for alerts
 */
export function startAlertPolling(walletAddress: string): void {
  if (pollInterval) {
    clearInterval(pollInterval);
  }

  const config = getAlertConfig();
  if (!config.enabled) return;

  // Initial check
  checkAlerts(walletAddress).catch(console.error);

  // Set up polling
  pollInterval = window.setInterval(() => {
    checkAlerts(walletAddress).catch(console.error);
  }, config.pollIntervalMs);
}

/**
 * Stop polling for alerts
 */
export function stopAlertPolling(): void {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format alert type for display
 */
export function formatAlertType(alert: PriceAlert): string {
  switch (alert.type) {
    case 'above':
      return `Price above $${alert.targetPrice?.toFixed(6)}`;
    case 'below':
      return `Price below $${alert.targetPrice?.toFixed(6)}`;
    case 'percent_up':
      return `Up ${alert.targetPercent}%`;
    case 'percent_down':
      return `Down ${alert.targetPercent}%`;
    default:
      return 'Unknown';
  }
}

/**
 * Format alert status for display
 */
export function formatAlertStatus(status: AlertStatus): { label: string; color: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', color: '#00f0ff' };
    case 'triggered':
      return { label: 'Triggered', color: '#00ff88' };
    case 'expired':
      return { label: 'Expired', color: '#888' };
    case 'cancelled':
      return { label: 'Cancelled', color: '#888' };
    default:
      return { label: 'Unknown', color: '#888' };
  }
}

/**
 * Calculate price change from base
 */
export function calculatePriceChange(basePrice: number, currentPrice: number): number {
  if (basePrice === 0) return 0;
  return ((currentPrice - basePrice) / basePrice) * 100;
}
